import { useState, type ChangeEvent, type KeyboardEvent } from 'react';
import {
  Button,
  Column,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Row,
  TextField,
  Typography,
} from '@tempmee/react';
import { SolidCheckCircleIcon } from '@tempmee/react-icons';
import { colors } from '@tempmee/tailwind';
import { formatDisplayDate } from './discountCodeView';
import { useApplyCode } from '~/domain/DiscountCode';

/** Shown when a failure carries no message of its own — a network drop, gateway, or an empty-bodied 5xx. */
const GENERIC_ERROR = 'Something went wrong. Please try again.';

/** Shown under the field when the API answers 200: the office already holds this code (GOTU-10730). */
const ALREADY_APPLIED = 'Code already applied';

/**
 * Office-facing copy for each refusal `reason` (GOTU-7283).
 *
 * The apply API returns a machine-readable `reason` alongside a `message` that is
 * composed for engineers — it carries campaign UUIDs, the internal word "opt-in",
 * raw UTC timestamps and interval notation. We branch on `reason` and render our
 * own copy; the raw `message` is never shown, so an unrecognised or absent reason
 * falls through to the generic line rather than leaking internal detail.
 */
const refusalMessage = (reason: string | undefined): string => {
  switch (reason) {
    case 'invalid_code':
      return "We don't recognize that code.";
    case 'promotion_ended':
      return 'That promotion has ended.';
    case 'not_eligible':
      return "That code isn't available to your office.";
    default:
      return GENERIC_ERROR;
  }
};

export interface IAddDiscountCodeDialogProps {
  /** Whether the dialog is visible. */
  open: boolean;
  /** Closes the dialog — Escape, the X, or dismissing the success view. */
  onClose: () => void;
  /** The location the code is applied to; the selected office when omitted. */
  officeId?: string;
}

/**
 * "Add a code" dialog for the Promo Codes section (GOTU-5656).
 *
 * Owns one job: take a code, POST it, and either confirm the applied code
 * (GOTU-7286) — the history list refreshes itself off the mutation's cache
 * invalidation — or name the failure inline under the field (GOTU-7283). A code
 * the office already holds (a 200, not a 201) gets "Code already applied" in that
 * same slot instead of the confirmation (GOTU-10730). It never renders the list
 * and never guesses at eligibility; the server is the only authority on whether a
 * code applies.
 */
export const AddDiscountCodeDialog = ({ open, onClose, officeId }: IAddDiscountCodeDialogProps) => {
  const [code, setCode] = useState('');
  const { mutate, isPending, isSuccess, data, error, reset } = useApplyCode(officeId);

  const trimmedCode = code.trim();

  // A 201 recorded a new application and gets the confirmation; a 200 means the
  // office already held the code, so nothing new is confirmed.
  const appliedCode = isSuccess && data.created ? data.appliedCode : undefined;
  const alreadyApplied = isSuccess && !data.created;

  const fieldMessage = error
    ? refusalMessage(error.response?.data?.reason)
    : alreadyApplied
      ? ALREADY_APPLIED
      : null;

  const closeAndReset = () => {
    setCode('');
    reset();
    onClose();
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCode(event.target.value);
    // Clear a stale failure (or "already applied") the moment the office edits the
    // code, so the field stops describing a code they are no longer entering.
    if (error || alreadyApplied) {
      reset();
    }
  };

  const handleApply = () => {
    if (!trimmedCode || isPending) {
      return;
    }

    // On success the mutation invalidates the history query itself, so the new
    // card appears behind this dialog; the success view (below) confirms it.
    mutate({ code: trimmedCode });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleApply();
    }
  };

  const expiresLabel = appliedCode ? formatDisplayDate(appliedCode.endsAt) : undefined;
  // A campaign-service older than #229 omits `professionTypes`: no line, not a throw.
  const professionTypeLabels = (appliedCode?.professionTypes ?? []).map(
    (professionType) => professionType.label,
  );

  return (
    <Dialog
      open={open}
      onClose={closeAndReset}
      // The office may be mid-way through typing a code; a stray click outside
      // must not throw it away. The X, Escape and Done still close it.
      closeOnOverlayClick={false}
      size="medium"
      aria-label="Add a promo code"
      data-cy="add-promo-code-dialog"
    >
      <DialogTitle onClose={closeAndReset} closeLabel="Close">
        <Typography variant="h6" color={colors.text.primary}>
          {appliedCode ? 'Code applied' : 'Add a code'}
        </Typography>
      </DialogTitle>

      {appliedCode ? (
        <>
          <DialogContent>
            <Row align="start" gap={3} data-cy="add-promo-code-success">
              <SolidCheckCircleIcon size={24} color={colors.success[600]} />
              <Column gap={1}>
                <Typography variant="body1" bold color={colors.text.primary}>
                  {appliedCode.description}
                </Typography>
                {professionTypeLabels.length > 0 && (
                  <Typography
                    variant="body2"
                    color={colors.text.secondary}
                    data-cy="add-promo-code-profession-types"
                  >
                    <Typography variant="body2" bold as="span">
                      Profession types:
                    </Typography>{' '}
                    {professionTypeLabels.join(', ')}
                  </Typography>
                )}
                {expiresLabel && (
                  <Typography variant="body2" color={colors.text.secondary}>
                    Expires {expiresLabel}
                  </Typography>
                )}
              </Column>
            </Row>
          </DialogContent>

          <DialogActions>
            <Button onClick={closeAndReset} data-cy="add-promo-code-done">
              Done
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogContent>
            <TextField
              label="Promo Code"
              value={code}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              error={Boolean(fieldMessage)}
              helperText={fieldMessage}
              disabled={isPending}
              autoFocus
              autoCapitalize="characters"
              autoComplete="off"
              data-cy="add-promo-code-input"
            />
          </DialogContent>

          <DialogActions>
            <Button
              onClick={handleApply}
              loading={isPending}
              disabled={!trimmedCode || isPending}
              data-cy="add-promo-code-submit"
            >
              Add
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
